import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Heart, Plus, Calendar, Droplet, Trash2, 
  Edit2, X, Check, TrendingUp, AlertCircle, Moon
} from 'lucide-react';
import { toast } from 'sonner';
import TimelineButton from '../components/TimelineButton';
import { todayLocal } from '../utils/dateUtils';

const CYCLE_SYMPTOMS = [
  { key: 'cramps', label: 'Cramps', icon: '😣' },
  { key: 'bloating', label: 'Bloating', icon: '🫄' },
  { key: 'headache', label: 'Headache', icon: '🤕' },
  { key: 'fatigue', label: 'Fatigue', icon: '😴' },
  { key: 'mood_changes', label: 'Mood Changes', icon: '🎭' },
  { key: 'breast_tenderness', label: 'Breast Tenderness', icon: '💢' },
  { key: 'acne', label: 'Acne', icon: '😖' },
  { key: 'food_cravings', label: 'Food Cravings', icon: '🍫' },
  { key: 'insomnia', label: 'Insomnia', icon: '😵' },
  { key: 'back_pain', label: 'Back Pain', icon: '🔙' },
  { key: 'nausea', label: 'Nausea', icon: '🤢' },
  { key: 'anxiety', label: 'Anxiety', icon: '😰' },
  { key: 'irritability', label: 'Irritability', icon: '😤' }
];

const MOODS = [
  { value: 'good', label: 'Good', emoji: '😊', color: 'text-green-500' },
  { value: 'neutral', label: 'Neutral', emoji: '😐', color: 'text-gray-500' },
  { value: 'low', label: 'Low', emoji: '😔', color: 'text-blue-500' },
  { value: 'anxious', label: 'Anxious', emoji: '😰', color: 'text-amber-500' },
  { value: 'irritable', label: 'Irritable', emoji: '😤', color: 'text-red-500' }
];

const FLOW_LEVELS = [
  { value: 'spotting', label: 'Spotting', color: 'bg-pink-200' },
  { value: 'light', label: 'Light', color: 'bg-pink-300' },
  { value: 'medium', label: 'Medium', color: 'bg-pink-400' },
  { value: 'heavy', label: 'Heavy', color: 'bg-pink-600' }
];

const CycleTracker = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [stats, setStats] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  
  const [formData, setFormData] = useState({
    date: todayLocal(),
    period_day: null,
    flow_level: null,
    symptoms: [],
    mood: null,
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
    fetchStats();
    fetchCorrelations();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/cycle?days=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching cycle entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/cycle/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching cycle stats:', error);
    }
  };

  const fetchCorrelations = async () => {
    try {
      const response = await axios.get(`${API}/cycle/correlations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrelations(response.data);
    } catch (error) {
      console.error('Error fetching correlations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        period_day: formData.period_day ? parseInt(formData.period_day) : null
      };

      await axios.post(`${API}/cycle`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(editingEntry ? 'Entry updated!' : 'Cycle entry saved!');
      resetForm();
      fetchEntries();
      fetchStats();
      fetchCorrelations();
    } catch (error) {
      console.error('Error saving cycle entry:', error);
      toast.error('Failed to save entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this cycle entry?')) return;
    
    try {
      await axios.delete(`${API}/cycle/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      period_day: entry.period_day || null,
      flow_level: entry.flow_level || null,
      symptoms: entry.symptoms || [],
      mood: entry.mood || null,
      notes: entry.notes || ''
    });
    setShowForm(true);
  };

  const toggleSymptom = (symptomKey) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptomKey)
        ? prev.symptoms.filter(s => s !== symptomKey)
        : [...prev.symptoms, symptomKey]
    }));
  };

  const resetForm = () => {
    setFormData({
      date: todayLocal(),
      period_day: null,
      flow_level: null,
      symptoms: [],
      mood: null,
      notes: ''
    });
    setEditingEntry(null);
    setShowForm(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-8" data-testid="cycle-tracker">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl p-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Heart className="w-10 h-10 text-pink-600" strokeWidth={1.5} />
              <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
                Cycle Tracker
              </h1>
            </div>
            <p className="text-[#57534E]">
              Track menstrual cycles to identify hormonal correlations with symptoms
            </p>
          </div>
          <TimelineButton />
        </div>
      </div>

      {/* Stats Overview */}
      {stats && stats.has_data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <Calendar className="w-6 h-6 mx-auto mb-2 text-pink-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">{stats.average_cycle_length}</p>
            <p className="text-sm text-[#57534E]">Avg Cycle Days</p>
          </Card>
          <Card className="p-4 text-center">
            <Droplet className="w-6 h-6 mx-auto mb-2 text-rose-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">{stats.average_period_length}</p>
            <p className="text-sm text-[#57534E]">Avg Period Days</p>
          </Card>
          <Card className="p-4 text-center">
            <Moon className="w-6 h-6 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">{stats.days_until_next}</p>
            <p className="text-sm text-[#57534E]">Days Until Next</p>
          </Card>
          <Card className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-indigo-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">{stats.cycles_tracked}</p>
            <p className="text-sm text-[#57534E]">Cycles Tracked</p>
          </Card>
        </div>
      )}

      {/* Prediction Card */}
      {stats && stats.has_data && (
        <Card className="p-6 bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="w-5 h-5 text-pink-600" />
            <h3 className="font-bold text-[#2D2D2D]">Next Period Prediction</h3>
          </div>
          <p className="text-[#57534E]">
            Based on your {stats.cycles_tracked} tracked cycles, your next period is predicted to start around{' '}
            <span className="font-semibold text-pink-600">{formatDate(stats.predicted_next_period)}</span>
          </p>
        </Card>
      )}

      {/* Add Entry Button */}
      {!showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-xl py-6"
          data-testid="add-cycle-entry-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Log Today's Cycle
        </Button>
      )}

      {/* Entry Form */}
      {showForm && (
        <Card className="p-6 border-2 border-pink-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#2D2D2D] flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" />
              {editingEntry ? 'Edit Cycle Entry' : 'Log Cycle'}
            </h2>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
                required
                data-testid="cycle-date-input"
              />
            </div>

            {/* Period Day */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">
                Period Day (leave blank if not on period)
              </Label>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setFormData({ ...formData, period_day: formData.period_day === day ? null : day })}
                    className={`w-10 h-10 rounded-full border-2 transition-all font-semibold ${
                      formData.period_day === day
                        ? 'border-pink-500 bg-pink-500 text-white'
                        : 'border-stone-200 hover:border-pink-300 text-[#57534E]'
                    }`}
                    data-testid={`period-day-${day}`}
                  >
                    {day}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, period_day: null })}
                  className={`px-4 h-10 rounded-full border-2 transition-all ${
                    formData.period_day === null
                      ? 'border-gray-500 bg-gray-100 text-gray-700'
                      : 'border-stone-200 hover:border-gray-300 text-[#57534E]'
                  }`}
                >
                  Not on period
                </button>
              </div>
            </div>

            {/* Flow Level */}
            {formData.period_day && (
              <div>
                <Label className="text-[#2D2D2D] font-medium mb-3 block">Flow Level</Label>
                <div className="flex gap-2 flex-wrap">
                  {FLOW_LEVELS.map(({ value, label, color }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, flow_level: formData.flow_level === value ? null : value })}
                      className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                        formData.flow_level === value
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-stone-200 hover:border-pink-300'
                      }`}
                      data-testid={`flow-${value}`}
                    >
                      <span className={`w-3 h-3 rounded-full ${color}`}></span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mood */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Mood</Label>
              <div className="flex gap-2 flex-wrap">
                {MOODS.map(({ value, label, emoji, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, mood: formData.mood === value ? null : value })}
                    className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                      formData.mood === value
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-stone-200 hover:border-pink-300'
                    }`}
                    data-testid={`mood-${value}`}
                  >
                    <span className="text-lg">{emoji}</span>
                    <span className={formData.mood === value ? color : ''}>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Symptoms</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {CYCLE_SYMPTOMS.map(({ key, label, icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleSymptom(key)}
                    className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-2 ${
                      formData.symptoms.includes(key)
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-stone-200 hover:border-stone-300 text-[#57534E]'
                    }`}
                    data-testid={`symptom-${key}`}
                  >
                    <span className="text-lg">{icon}</span>
                    <span className="text-sm font-medium">{label}</span>
                    {formData.symptoms.includes(key) && <Check className="w-4 h-4 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional observations..."
                className="mt-1"
                rows={3}
                data-testid="cycle-notes-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="cycle-submit-btn"
              >
                {editingEntry ? 'Update Entry' : 'Save Entry'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Correlations */}
      {correlations && correlations.has_data && (
        <Card className="p-6">
          <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Cycle & Flare Correlations
          </h2>
          
          <div className="space-y-3">
            {correlations.correlations.slice(0, 5).map((corr, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{corr.phase}</p>
                  <p className="text-sm text-[#57534E]">{corr.insight}</p>
                </div>
                <div className="text-right">
                  <p className={`text-xl font-bold ${corr.flare_rate > 50 ? 'text-red-500' : corr.flare_rate > 25 ? 'text-amber-500' : 'text-green-500'}`}>
                    {corr.flare_rate}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Entries List */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2D2D2D]">Recent Entries</h2>
        
        {loading ? (
          <p className="text-center text-[#57534E] py-8">Loading...</p>
        ) : entries.length === 0 ? (
          <Card className="p-8 text-center">
            <Heart className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-[#57534E]">No cycle entries yet. Start tracking to see patterns!</p>
          </Card>
        ) : (
          entries.slice(0, 14).map((entry) => (
            <Card key={entry.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`cycle-entry-${entry.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-[#2D2D2D]">{formatDate(entry.date)}</span>
                    {entry.period_day && (
                      <span className="text-sm bg-pink-100 text-pink-700 px-2 py-1 rounded-full">
                        Day {entry.period_day}
                      </span>
                    )}
                    {entry.flow_level && (
                      <span className="text-sm text-[#57534E] capitalize">{entry.flow_level} flow</span>
                    )}
                    {entry.mood && (
                      <span className="text-lg">{MOODS.find(m => m.value === entry.mood)?.emoji}</span>
                    )}
                  </div>
                  
                  {entry.symptoms && entry.symptoms.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {entry.symptoms.map(symptom => (
                        <span key={symptom} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded capitalize">
                          {symptom.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {entry.notes && (
                    <p className="text-sm text-[#57534E] mt-2 italic">{entry.notes}</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(entry.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default CycleTracker;
